import React, { useState } from 'react';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import ChecklistOutlined from '@mui/icons-material/ChecklistOutlined';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import { Button, Input, Badge, Modal } from '../../../components/ui';

/**
 * Search, date range and bulk actions.
 *
 * QDFMainPage used to render this component three separate times behind
 * different breakpoint wrappers. It now renders once: the search box is always
 * visible, and the date filters move into a sheet on mobile instead of stacking
 * into a tall column that pushed the results off-screen.
 */
const SearchAndFilters = ({
  searchTerm,
  startDate,
  endDate,
  totalResults,
  filteredResults,
  visibleCount,
  onSearchChange,
  onStartDateChange,
  onEndDateChange,
  onClearFilters,
  isSelectionMode,
  selectedCards,
  paginatedEntries,
  userRole,
  isBulkDownloading,
  onToggleSelectionMode,
  onSelectAll,
  onBulkDownload,
  onBulkDelete,
}) => {
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  const activeFilterCount = [startDate, endDate].filter(Boolean).length;
  const selectedCount = selectedCards?.size || 0;
  const allSelected = selectedCount > 0 && selectedCount === paginatedEntries?.length;

  const dateFields = (
    <div className="grid gap-4 sm:grid-cols-2">
      <Input label="From date" type="date" value={startDate} onChange={onStartDateChange} />
      <Input label="To date" type="date" value={endDate} onChange={onEndDateChange} />
    </div>
  );

  return (
    <div className="mb-4 rounded-lg border border-border bg-surface p-4 shadow-sm">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
        <div className="relative flex-1">
          <Input
            label="Search work orders"
            type="search"
            value={searchTerm}
            onChange={onSearchChange}
            placeholder="Document number, location, asset, status…"
            className="[&_input]:pl-10"
          />
          <SearchOutlined
            style={{ fontSize: 20 }}
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-[2.15rem] text-muted-fg"
          />
        </div>

        {/* Date inputs inline from lg; behind a sheet below that. */}
        <div className="hidden lg:block lg:w-[22rem]">{dateFields}</div>

        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            icon={TuneOutlined}
            onClick={() => setFilterSheetOpen(true)}
            className="flex-1 lg:hidden"
          >
            Dates
            {activeFilterCount > 0 && (
              <Badge variant="info" size="sm" className="ml-1">
                {activeFilterCount}
              </Badge>
            )}
          </Button>

          {(searchTerm || activeFilterCount > 0) && (
            <Button variant="ghost" icon={CloseOutlined} onClick={onClearFilters}>
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Result summary + the filters currently narrowing it. */}
      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3 text-sm text-muted-fg">
        <span>
          Showing <span className="tabular font-medium text-foreground">{visibleCount ?? filteredResults}</span> of{' '}
          <span className="tabular font-medium text-foreground">{totalResults}</span> work orders
        </span>
        {searchTerm && <Badge variant="info" size="sm">“{searchTerm}”</Badge>}
        {startDate && <Badge variant="neutral" size="sm">From {new Date(startDate).toLocaleDateString('en-GB')}</Badge>}
        {endDate && <Badge variant="neutral" size="sm">To {new Date(endDate).toLocaleDateString('en-GB')}</Badge>}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button
          variant={isSelectionMode ? 'secondary' : 'ghost'}
          size="sm"
          icon={isSelectionMode ? CloseOutlined : ChecklistOutlined}
          onClick={onToggleSelectionMode}
        >
          {isSelectionMode ? 'Cancel selection' : 'Select'}
        </Button>

        {isSelectionMode && (
          <>
            <Button variant="secondary" size="sm" onClick={onSelectAll}>
              {allSelected ? 'Deselect all' : 'Select all'}
            </Button>

            {selectedCount > 0 && (
              <>
                <Button
                  variant="accent"
                  size="sm"
                  icon={DownloadOutlined}
                  onClick={onBulkDownload}
                  loading={isBulkDownloading}
                >
                  {isBulkDownloading ? `Creating ZIP (${selectedCount})…` : `Download ZIP (${selectedCount})`}
                </Button>

                {userRole === 'admin' && (
                  <Button variant="danger-soft" size="sm" icon={DeleteOutline} onClick={onBulkDelete}>
                    Delete ({selectedCount})
                  </Button>
                )}
              </>
            )}
          </>
        )}
      </div>

      <Modal
        isOpen={filterSheetOpen}
        onClose={() => setFilterSheetOpen(false)}
        title="Filter by date"
        size="sm"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                onClearFilters();
                setFilterSheetOpen(false);
              }}
            >
              Clear
            </Button>
            <Button onClick={() => setFilterSheetOpen(false)}>Apply</Button>
          </>
        }
      >
        {dateFields}
      </Modal>
    </div>
  );
};

export default SearchAndFilters;
